import * as React from 'react'
import * as LabelPrimitive from '@radix-ui/react-label'
import { Slot } from '@radix-ui/react-slot'
import {
  Controller,
  FormProvider,
  useFormContext,
  useFormState,
  type ControllerProps,
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
  type UseFormReturn,
} from 'react-hook-form'

import { cn } from '@/lib/utils'
import { Label } from './label'
import { createContext, useContext } from 'react'

type customKey = 'activeLight' | 'showLabel'
type ContextProp = {
  customProp?: Partial<Record<customKey, boolean>>
}
const FormIsFilterContext = createContext<ContextProp>({
  customProp: {},
})
export const useFormIsFilter = () => useContext(FormIsFilterContext)
type FormProps<T extends FieldValues = FieldValues> = Omit<UseFormReturn<T>, 'children'> &
  Pick<React.ComponentProps<typeof FormProvider>, 'children'> &
  ContextProp
function Form<T extends FieldValues = FieldValues>({ customProp = {}, children, ...formProps }: FormProps<T>) {
  return (
    <FormIsFilterContext.Provider
      value={{
        customProp,
      }}
    >
      <FormProvider {...formProps}>{children}</FormProvider>
    </FormIsFilterContext.Provider>
  )
}

// const Form = FormProvider

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName
}
type RenderSourceArgs = Parameters<ControllerProps['render']>[0]
type ExtendRender<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  render: (
    args: Omit<RenderSourceArgs, 'field'> & {
      field: RenderSourceArgs['field'] & {
        onValueChange?: (value?: FieldPathValue<TFieldValues, TName>) => void
        onCheckedChange?: (value?: FieldPathValue<TFieldValues, TName>) => void
      }
    },
  ) => React.ReactElement
}
const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue)

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: Omit<ControllerProps<TFieldValues, TName>, 'render'> & ExtendRender<TFieldValues, TName>) => {
  const { customProp } = useFormIsFilter() // 新增：获取 isFilter
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller
        {...props}
        render={({ field: sourceField, formState, fieldState }) => {
          // 支持onValueChange事件
          const onValueChange = (value: FieldPathValue<TFieldValues, TName>) => {
            sourceField.onChange(value)
          }
          const onCheckedChange = (value: FieldPathValue<TFieldValues, TName>) => {
            sourceField.onChange(value)
          }
          return props.render({
            field: {
              ...sourceField,
              onValueChange,
              onCheckedChange,
              ...customProp,
            },
            formState,
            fieldState,
          })
        }}
      />
    </FormFieldContext.Provider>
  )
}

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext)
  const itemContext = React.useContext(FormItemContext)
  const { getFieldState } = useFormContext()
  const formState = useFormState({ name: fieldContext.name })
  const fieldState = getFieldState(fieldContext.name, formState)

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>')
  }

  const { id } = itemContext

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  }
}

type FormItemContextValue = {
  id: string
}

const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue)

function FormItem({ className, ...props }: React.ComponentProps<'div'>) {
  const id = React.useId()

  return (
    <FormItemContext.Provider value={{ id }}>
      <div data-slot="form-item" className={cn('grid gap-4', className)} {...props} />
    </FormItemContext.Provider>
  )
}

function FormLabel({ className, ...props }: React.ComponentProps<typeof LabelPrimitive.Root>) {
  const { error, formItemId } = useFormField()

  return (
    <Label
      data-slot="form-label"
      data-error={!!error}
      className={cn('font-normal data-[error=true]:text-danger-foreground pt-2', className)}
      htmlFor={formItemId}
      {...props}
    />
  )
}

function FormControl({ ...props }: React.ComponentProps<typeof Slot>) {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField()

  return (
    <Slot
      data-slot="form-control"
      id={formItemId}
      aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
      aria-invalid={!!error}
      {...props}
    />
  )
}

function FormDescription({ className, ...props }: React.ComponentProps<'p'>) {
  const { formDescriptionId } = useFormField()

  return (
    <p
      data-slot="form-description"
      id={formDescriptionId}
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  )
}

function FormMessage({ className, ...props }: React.ComponentProps<'p'>) {
  const { error, formMessageId } = useFormField()
  const body = error ? String(error?.message ?? '') : props.children

  if (!body) {
    return null
  }

  return (
    <p data-slot="form-message" id={formMessageId} className={cn('text-danger text-sm', className)} {...props}>
      {body}
    </p>
  )
}

export { useFormField, Form, FormItem, FormLabel, FormControl, FormDescription, FormMessage, FormField }
